/**
 * AI "Clean up" helper for staff FAQ-answer authoring + per-tenant usage readout. Staff-only,
 * human-in-the-loop: the endpoint only ever PROPOSES a rewrite — the UI shows it in a preview panel
 * and staff must click "Use this" to apply it (RichTextEditor never auto-applies/saves).
 *
 * Live + mock implementations, wired through the USE_MOCKS facade in index.ts (same pattern as
 * every other api/*.ts module — see staffApi.ts / mockDb.ts).
 */
import { apiFetch, ApiError } from "./client.js";
import type { AiCleanupMode } from "./types.js";

/** Distinct error code the UI checks for the fail-closed "AI cleanup isn't enabled yet" case
 *  (503 ai_not_configured — no Anthropic API key secret set) vs. a real upstream failure
 *  (502 ai_unavailable). */
export const AI_NOT_CONFIGURED = "ai_not_configured";

// ---- Live --------------------------------------------------------------------

/** POST /api/admin/ai/cleanup — adminAuth. tenantId comes from the auth context server-side. */
export async function cleanupAnswerLive(text: string, mode: AiCleanupMode): Promise<string> {
  const res = await apiFetch<{ suggestion: string }>("/admin/ai/cleanup", {
    method: "POST",
    auth: true,
    body: { text, mode },
  });
  return res.suggestion;
}

// ---- Mock ----------------------------------------------------------------------

/** Deterministic fake "cleanup" for USE_MOCKS/dev/tests — tags the mode so the demo/manual-QA
 *  reviewer can tell the two modes apart without reading the diff closely. Never mutates input. */
export async function cleanupAnswerMock(text: string, mode: AiCleanupMode): Promise<string> {
  const trimmed = text.trim();
  if (trimmed === "" || trimmed === "<p></p>") {
    throw new ApiError("Nothing to clean up yet — write a draft first.", 400, "validation_error", false);
  }
  const label = mode === "plain" ? "Plain-language rewrite" : "Cleaned-up wording";
  return `<p><em>${label} (mock preview):</em></p>${text}`;
}
